import React, { useState, useEffect } from "react";
import {
  collection, getDocs, addDoc, updateDoc, doc,
  serverTimestamp, setDoc, deleteDoc, getDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

export default function AdminPanel() {
  const { currentUser } = useAuth();
  const [partners, setPartners] = useState([]);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stakes");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // New partner/whitelist form
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("partner");

  // Stake adjustment
  const [adjustEmail, setAdjustEmail] = useState("");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustNote, setAdjustNote] = useState("");
  const [adjustDate, setAdjustDate] = useState(new Date().toISOString().split("T")[0]);
  const [adjustType, setAdjustType] = useState("add");

  // Config
  const [cutInDate, setCutInDate] = useState("");

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    setLoading(true);
    try {
      const [pSnap, wSnap, configDoc] = await Promise.all([
        getDocs(collection(db, "partners")),
        getDocs(collection(db, "whitelist")),
        getDoc(doc(db, "config", "settings")),
      ]);
      setPartners(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setWhitelist(wSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      if (configDoc.exists()) setCutInDate(configDoc.data().cutInDate || "");
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  function flash(msg, isError = false) {
    if (isError) { setError(msg); setTimeout(() => setError(""), 4000); }
    else { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
  }

  async function addPartnerAndWhitelist() {
    if (!newEmail || !newName) return flash("Name and email are required.", true);
    try {
      const partnerRef = doc(db, "partners", newEmail);
      await setDoc(partnerRef, { email: newEmail, name: newName, totalStake: 0, createdAt: serverTimestamp() });
      await setDoc(doc(db, "whitelist", newEmail), { email: newEmail, name: newName, role: newRole, addedBy: currentUser.email, createdAt: serverTimestamp() });
      flash(`✓ ${newName} added as ${newRole}`);
      setNewEmail(""); setNewName(""); setNewRole("partner");
      fetchAll();
    } catch (e) { flash("Failed to add user: " + e.message, true); }
  }

  async function removeFromWhitelist(email) {
    if (!window.confirm(`Remove ${email} from whitelist? They will lose access immediately.`)) return;
    try {
      await deleteDoc(doc(db, "whitelist", email));
      flash(`✓ ${email} removed from whitelist`);
      fetchAll();
    } catch (e) { flash("Failed to remove: " + e.message, true); }
  }

  async function updateRole(email, role) {
    try {
      await updateDoc(doc(db, "whitelist", email), { role });
      flash(`✓ Role updated to ${role}`);
      fetchAll();
    } catch (e) { flash("Failed to update role", true); }
  }

  async function adjustStake() {
    if (!adjustEmail || !adjustAmount) return flash("Select a partner and enter an amount.", true);
    const amount = adjustType === "remove" ? -Math.abs(parseFloat(adjustAmount)) : Math.abs(parseFloat(adjustAmount));
    const partner = partners.find(p => p.email === adjustEmail);
    if (!partner) return flash("Partner not found", true);

    const newTotal = (partner.totalStake || 0) + amount;
    if (newTotal < 0) return flash("Stake cannot go below $0.", true);

    try {
      await updateDoc(doc(db, "partners", partner.id), { totalStake: newTotal, updatedAt: serverTimestamp() });
      await addDoc(collection(db, "transactions"), {
        partnerEmail: partner.email,
        partnerName: partner.name,
        amount,
        type: adjustType === "add" ? "Manual Addition" : "Manual Reduction",
        note: adjustNote || "Manual admin adjustment",
        date: new Date(adjustDate),
        addedBy: currentUser.email,
        createdAt: serverTimestamp(),
      });
      flash(`✓ Stake ${adjustType === "add" ? "increased" : "decreased"} by $${Math.abs(amount).toLocaleString()} for ${partner.name}`);
      setAdjustAmount(""); setAdjustNote(""); setAdjustEmail("");
      fetchAll();
    } catch (e) { flash("Failed to adjust stake: " + e.message, true); }
  }

  async function saveCutInDate() {
    try {
      await setDoc(doc(db, "config", "settings"), { cutInDate, updatedAt: serverTimestamp() }, { merge: true });
      flash("✓ Cut-in date saved");
    } catch (e) { flash("Failed to save date", true); }
  }

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
      <div style={{ width: "36px", height: "36px", border: "3px solid var(--border)", borderTop: "3px solid var(--accent)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
    </div>
  );

  return (
    <div style={styles.page} className="animate-in">
      <div style={styles.header}>
        <h1 style={styles.title}>Admin Panel</h1>
        <p style={styles.subtitle}>Manage partners, stakes, and access</p>
      </div>

      {success && <div style={styles.successBox}>{success}</div>}
      {error && <div style={styles.errorBox}>⚠ {error}</div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        {[
          { key: "stakes", label: "⬡ Adjust Stakes" },
          { key: "users", label: "◈ Manage Users" },
          { key: "config", label: "⚙ Settings" },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* STAKES TAB */}
      {activeTab === "stakes" && (
        <div style={styles.tabContent}>
          <div className="card" style={{ padding: "24px", marginBottom: "20px" }}>
            <h2 style={{ ...styles.cardTitle, marginBottom: "20px" }}>Manual Stake Adjustment</h2>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Partner</label>
                <select value={adjustEmail} onChange={e => setAdjustEmail(e.target.value)} className="input-field">
                  <option value="">Select partner...</option>
                  {partners.map(p => <option key={p.email} value={p.email}>{p.name} (${(p.totalStake || 0).toLocaleString()})</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Action</label>
                <div style={styles.toggleRow}>
                  {["add", "remove"].map(t => (
                    <button key={t} onClick={() => setAdjustType(t)} style={{
                      ...styles.toggleBtn,
                      background: adjustType === t ? (t === "add" ? "var(--green-dim)" : "var(--red-dim)") : "var(--bg-secondary)",
                      color: adjustType === t ? (t === "add" ? "var(--green)" : "var(--red)") : "var(--text-muted)",
                      border: `1px solid ${adjustType === t ? (t === "add" ? "rgba(0,214,143,0.3)" : "rgba(255,92,108,0.3)") : "var(--border)"}`,
                    }}>
                      {t === "add" ? "➕ Add Stake" : "➖ Remove Stake"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={styles.label}>Amount ($)</label>
                <input type="number" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="0.00" className="input-field" min="0" />
              </div>
              <div>
                <label style={styles.label}>Date</label>
                <input type="date" value={adjustDate} onChange={e => setAdjustDate(e.target.value)} className="input-field" />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={styles.label}>Note</label>
                <input type="text" value={adjustNote} onChange={e => setAdjustNote(e.target.value)} placeholder="Reason for adjustment..." className="input-field" />
              </div>
            </div>
            <button className="btn-primary" onClick={adjustStake} style={{ marginTop: "16px" }}>
              Apply Adjustment
            </button>
          </div>

          {/* Current stakes table */}
          <div className="card" style={{ padding: "24px" }}>
            <h2 style={{ ...styles.cardTitle, marginBottom: "16px" }}>Current Stakes</h2>
            <table style={styles.table}>
              <thead>
                <tr>{["Partner", "Email", "Total Stake", "% Ownership"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {partners.map((p, i) => {
                  const total = partners.reduce((s, x) => s + (x.totalStake || 0), 0);
                  const pct = total > 0 ? ((p.totalStake / total) * 100).toFixed(2) : "0.00";
                  return (
                    <tr key={p.id} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                      <td style={{ ...styles.td, fontWeight: 600 }}>{p.name}</td>
                      <td style={{ ...styles.td, color: "var(--text-muted)", fontSize: "13px" }}>{p.email}</td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>${(p.totalStake || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                      <td style={{ ...styles.td, color: "var(--accent-bright)", fontWeight: 700 }}>{pct}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* USERS TAB */}
      {activeTab === "users" && (
        <div style={styles.tabContent}>
          <div className="card" style={{ padding: "24px", marginBottom: "20px" }}>
            <h2 style={{ ...styles.cardTitle, marginBottom: "20px" }}>Add New User</h2>
            <div style={styles.formGrid}>
              <div>
                <label style={styles.label}>Full Name</label>
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="John Smith" className="input-field" />
              </div>
              <div>
                <label style={styles.label}>Google Email</label>
                <input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="john@gmail.com" type="email" className="input-field" />
              </div>
              <div>
                <label style={styles.label}>Role</label>
                <select value={newRole} onChange={e => setNewRole(e.target.value)} className="input-field">
                  <option value="partner">Partner (read-only)</option>
                  <option value="admin">Admin (read + write)</option>
                </select>
              </div>
            </div>
            <button className="btn-primary" onClick={addPartnerAndWhitelist} style={{ marginTop: "16px" }}>
              Add to Whitelist & Partners
            </button>
          </div>

          <div className="card" style={{ padding: "24px" }}>
            <h2 style={{ ...styles.cardTitle, marginBottom: "16px" }}>Access Whitelist ({whitelist.length})</h2>
            <table style={styles.table}>
              <thead>
                <tr>{["Name", "Email", "Role", "Added By", "Actions"].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {whitelist.map((u, i) => (
                  <tr key={u.id} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{u.name}</td>
                    <td style={{ ...styles.td, color: "var(--text-muted)", fontSize: "13px" }}>{u.email}</td>
                    <td style={styles.td}>
                      <select
                        value={u.role}
                        onChange={e => updateRole(u.email, e.target.value)}
                        style={{ background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: "6px", color: "var(--text-primary)", padding: "4px 8px", fontSize: "12px" }}
                      >
                        <option value="partner">partner</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td style={{ ...styles.td, color: "var(--text-muted)", fontSize: "12px" }}>{u.addedBy || "—"}</td>
                    <td style={styles.td}>
                      {u.email !== currentUser.email && (
                        <button className="btn-danger" onClick={() => removeFromWhitelist(u.email)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* CONFIG TAB */}
      {activeTab === "config" && (
        <div className="card" style={{ padding: "24px", maxWidth: "480px" }}>
          <h2 style={{ ...styles.cardTitle, marginBottom: "20px" }}>App Settings</h2>
          <div>
            <label style={styles.label}>Hard Cut-In Date</label>
            <input type="date" value={cutInDate} onChange={e => setCutInDate(e.target.value)} className="input-field" />
            <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "6px" }}>
              This is the date of the last finalized audit. Displayed on the dashboard and used as the historical starting point.
            </p>
          </div>
          <button className="btn-primary" onClick={saveCutInDate} style={{ marginTop: "16px" }}>
            Save Settings
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: "1000px", margin: "0 auto" },
  header: { marginBottom: "24px" },
  title: { fontSize: "28px", fontFamily: "var(--font-display)", fontWeight: 800 },
  subtitle: { color: "var(--text-muted)", fontSize: "14px", marginTop: "4px" },
  cardTitle: { fontSize: "16px", fontFamily: "var(--font-display)", fontWeight: 700 },
  tabs: { display: "flex", gap: "8px", marginBottom: "20px" },
  tab: { padding: "10px 20px", borderRadius: "var(--radius-sm)", background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "14px", fontWeight: 500, cursor: "pointer", transition: "var(--transition)" },
  tabActive: { background: "var(--accent-dim)", border: "1px solid var(--border-accent)", color: "var(--accent-bright)" },
  tabContent: {},
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" },
  label: { display: "block", fontSize: "11px", color: "var(--text-muted)", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" },
  toggleRow: { display: "flex", gap: "8px" },
  toggleBtn: { flex: 1, padding: "10px", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: "13px", fontWeight: 600, transition: "var(--transition)" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "10px 12px", fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid var(--border)" },
  td: { padding: "13px 12px", fontSize: "14px", color: "var(--text-primary)", borderBottom: "1px solid rgba(255,255,255,0.03)" },
  successBox: { background: "var(--green-dim)", border: "1px solid rgba(0,214,143,0.2)", borderRadius: "var(--radius-sm)", padding: "12px 16px", color: "var(--green)", fontWeight: 600, fontSize: "13px", marginBottom: "16px" },
  errorBox: { background: "var(--red-dim)", border: "1px solid rgba(255,92,108,0.2)", borderRadius: "var(--radius-sm)", padding: "12px 16px", color: "var(--red)", fontSize: "13px", marginBottom: "16px" },
};
